import { DocumentNode, FetchResult, useApolloClient } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import { useAuthContext } from '../contexts/AuthContext';
import { RoomDataType } from '../contexts/MessagesContext';
import { useSessionContext } from '../contexts/SessionContext';
import {
  DocumentSortFieldInput,
  ExpenseSortFieldInput,
  ListingDocument,
  ListingDocumentsDocument,
  ListingDocumentsQuery,
  ListingExpensesDocument,
  ListingExpensesQuery,
  ListingQuery,
  NotesDocument,
  NotesQuery,
  NoteTag,
  RoomType,
} from '../types/api.graphql';
import {
  ListingActivitySubscriptionGQL,
  ListingDocumentRemovedSubscriptionGQL,
  ListingDocumentSubscriptionGQL,
  ListingExpenseRemovedSubscriptionGQL,
  ListingExpenseSubscriptionGQL,
  ListingInternalValuationAppraisalGQL,
  ListingInternalValuationOfferSubscriptionGQL,
  ListingInternalValuationValuationSubscriptionGQL,
  ListingLiveActivitySubscriptionGQL,
  ListingNotesSubscriptionGQL,
  ListingPurchaseInformationSubscriptionGQL,
  ListingSaleInformationSubscriptionGQL,
  ListingStatusesSubscriptionGQL,
  ListingTagsSubscriptionGQL,
  ValuationsSubscriptionGQL,
} from '../types/subscriptions/listingDetails';
import { logErrorToMonitorSystem } from '../utils/errorLogging';

export const useSafeListingSubscription = (
  listingId: string,
  subscriptionGQL: DocumentNode,
  onData: (response: FetchResult<any, Record<string, any>, Record<string, any>>) => void,
  skip = false,
  parameters: Record<string, any> = undefined,
) => {
  const { client } = useAuthContext();
  const onDataRef = useRef<typeof onData>();
  onDataRef.current = onData;

  useEffect(() => {
    let isSubscribed = !skip;
    const subscription = skip
      ? null
      : client
          .subscribe({
            query: subscriptionGQL,
            variables: { listingId, ...parameters },
          })
          .subscribe({
            next(response) {
              if (isSubscribed) {
                onDataRef.current?.(response);
              }
            },
            error(err) {
              if (isSubscribed) {
                logErrorToMonitorSystem(`Error on subscription: ${subscriptionGQL} ${err}`);
              }
            },
            complete: () => {
              // we need to handle this to handle errors like 'Forbidden listing'
              if (isSubscribed) {
                onDataRef.current?.(undefined);
              }
            },
          });

    return () => {
      if (isSubscribed) {
        isSubscribed = false;
        subscription?.unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, listingId, skip]);
};

/**
 * Common structure for listing subscriptions
 * @param listingId - id of the listing
 * @param subscriptionGQL - subscription query
 * @param getCacheData - function to get the data from the subscription (and eventually the cached data) and return the updated cache data
 */
function useListingSubscription(
  listingId: string,
  subscriptionGQL: DocumentNode,
  getCacheData: (newData: any, cachedData?: ListingQuery['listing']) => Partial<ListingQuery['listing']>,
) {
  const { cache } = useApolloClient();

  useSafeListingSubscription(listingId, subscriptionGQL, (response) => {
    // Handle new data
    const listingCachedData: ListingQuery = cache?.readQuery({
      query: ListingDocument,
      variables: {
        listingId,
      },
    });
    cache?.writeQuery({
      query: ListingDocument,
      variables: {
        listingId,
      },
      data: {
        listing: {
          ...listingCachedData?.listing,
          ...getCacheData(response.data, listingCachedData?.listing),
        },
      },
    });
  });
}

export const useListingTagsSubscription = (listingId: string) =>
  useListingSubscription(listingId, ListingTagsSubscriptionGQL, (data) => ({ tags: data?.listingTags }));

export const useListingStatusesSubscription = (listingId: string) =>
  useListingSubscription(listingId, ListingStatusesSubscriptionGQL, (data, cachedData) => ({
    statuses: [...(cachedData.statuses || []), data?.listingCurrentStatus],
    currentStatus: data?.listingCurrentStatus,
  }));

export const useListingActivitySubscription = (listingId: string) =>
  useListingSubscription(listingId, ListingActivitySubscriptionGQL, (data) => ({
    activity: data?.listingSharedActivity,
  }));

export const useListingInternalValuationAppraisalSubscription = (listingId: string) =>
  useListingSubscription(listingId, ListingInternalValuationAppraisalGQL, (data, cachedData) => {
    const updatedAppraisalData = [...(cachedData?.appraisals || [])];
    const existentAppraisalIndex = cachedData?.appraisals?.findIndex(
      (appraisal) => appraisal?.id === data?.listingAppraisal?.id,
    );

    if (existentAppraisalIndex !== -1) {
      updatedAppraisalData[existentAppraisalIndex] = {
        ...updatedAppraisalData[existentAppraisalIndex],
        ...data?.listingAppraisal,
      };
    } else {
      updatedAppraisalData.push(data?.listingAppraisal);
    }

    return {
      appraisals: updatedAppraisalData,
    };
  });

export const useListingInternalValuationValuationSubscription = (listingId: string) =>
  useListingSubscription(listingId, ListingInternalValuationValuationSubscriptionGQL, (data, cachedData) => {
    const updatedValuation = data?.listingInternalValuation;
    const existentValuationHistory = cachedData?.internalValuationHistory;

    return {
      currentInternalValuation: updatedValuation,
      internalValuationHistory: [updatedValuation, ...(existentValuationHistory || [])],
    };
  });

export const useListingInternalValuationOfferSubscription = (listingId: string) =>
  useListingSubscription(listingId, ListingInternalValuationOfferSubscriptionGQL, (data, cachedData) => {
    const updatedOffer = data?.listingOffer;
    const existentOfferHistory = cachedData?.offerHistory;

    return {
      currentOffer: updatedOffer,
      offerHistory: [updatedOffer, ...(existentOfferHistory || [])],
    };
  });

export const useNotesSubscription = (listingId: string, selectedNotesTagFilter: NoteTag) => {
  const { cache } = useApolloClient();
  const { me } = useSessionContext();

  const skip = !me?.id;
  useSafeListingSubscription(
    listingId,
    ListingNotesSubscriptionGQL,
    (response) => {
      const notesCachedData: NotesQuery = cache?.readQuery({
        query: NotesDocument,
        variables: {
          listingId,
          ...(selectedNotesTagFilter && { tag: selectedNotesTagFilter }),
        },
      });

      const notesNodesCopy = [...(notesCachedData?.notes?.nodes || [])];

      if (response.data?.listingNote?.userOnly) {
        if (response.data?.listingNote?.submitter?.id === me?.id) {
          notesNodesCopy.push(response.data?.listingNote);
        } else {
          // If the note appears in the cached data, we must remove it
          const noteIndex = notesNodesCopy.findIndex((note) => note?.id === response.data?.listingNote?.id);
          if (noteIndex !== -1) {
            notesNodesCopy.splice(noteIndex, 1);
          }
        }
      } else notesNodesCopy.push(response.data?.listingNote);

      cache?.writeQuery({
        query: NotesDocument,
        variables: {
          listingId,
          ...(selectedNotesTagFilter && { tag: selectedNotesTagFilter }),
        },
        data: {
          notes: {
            ...notesCachedData.notes,
            nodes: notesNodesCopy,
            totalCount: notesCachedData.notes.totalCount + 1,
          },
        },
      });
    },
    skip,
  );
};

export const useListingDocumentsSubscription = (listingId: string, sort: DocumentSortFieldInput) => {
  const { cache } = useApolloClient();

  // Handle new/updated documents
  useSafeListingSubscription(listingId, ListingDocumentSubscriptionGQL, (response) => {
    const documentsCachedData: ListingDocumentsQuery = cache?.readQuery({
      query: ListingDocumentsDocument,
      variables: { listingId, sort },
    });

    const documentsNodesCopy = [...(documentsCachedData?.listing?.documents || [])];
    const newDocument = response.data?.listingDocument;

    const documentIndex = documentsNodesCopy.findIndex((doc) => doc?.id === newDocument?.id);
    if (documentIndex !== -1) {
      documentsNodesCopy[documentIndex] = newDocument;
    } else {
      documentsNodesCopy.unshift(newDocument);
    }

    cache?.writeQuery({
      query: ListingDocumentsDocument,
      variables: { listingId, sort },
      data: {
        listing: {
          ...documentsCachedData?.listing,
          documents: documentsNodesCopy,
        },
      },
    });
  });

  // Handle removed documents
  useSafeListingSubscription(listingId, ListingDocumentRemovedSubscriptionGQL, (response) => {
    const documentsCachedData: ListingDocumentsQuery = cache?.readQuery({
      query: ListingDocumentsDocument,
      variables: { listingId, sort },
    });

    const documentsNodesCopy = [...(documentsCachedData?.listing?.documents || [])];
    const removedDocument = response.data?.listingDocumentRemoved;

    const documentIndex = documentsNodesCopy.findIndex((doc) => doc?.id === removedDocument?.id);
    if (documentIndex !== -1) {
      documentsNodesCopy.splice(documentIndex, 1);

      cache?.writeQuery({
        query: ListingDocumentsDocument,
        variables: { listingId, sort },
        data: {
          listing: {
            ...documentsCachedData?.listing,
            documents: documentsNodesCopy,
          },
        },
      });
    }
  });
};

export const useListingValuationsSubscription = (listingId: string) =>
  useListingSubscription(listingId, ValuationsSubscriptionGQL, (data, cachedData) => ({
    valuations: [...(cachedData.valuations || []), data?.listingValuation],
  }));

export interface ActivityUser {
  userId: string;
  firstName: string;
  lastName: string;
  imageUrl?: string;
  isConnected?: boolean;
  lastActiveAt: string;
}

const useLiveActivitySubscription = (
  listingId: string,
  onUpdate: (users: ActivityUser[]) => void,
  parameters: Record<string, any> = undefined,
) => {
  const { me } = useSessionContext();
  const [usersActivity, setUsersActivity] = useState<ActivityUser[]>([]);

  const getUserFromSubscriptionData = (user: any) => {
    const userRet: ActivityUser = user.user;
    userRet.userId = user.userId;
    userRet.isConnected = user.isConnected;
    userRet.lastActiveAt = user.lastActiveAt;
    return userRet;
  };

  const updateUsers = (users: ActivityUser[]) => {
    const sortedUsers = users.sort(
      (a, b) =>
        Number(b.isConnected) - Number(a.isConnected) ||
        new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime(),
    );
    setUsersActivity(sortedUsers);
    onUpdate(sortedUsers);
  };

  const addOrUpdateUser = (userIncoming: any) => {
    const usersTemp = [...usersActivity];
    const userExisting = usersActivity.find((user) => user.userId === userIncoming.userId);
    if (userExisting) {
      userExisting.isConnected = userIncoming.isConnected;
      userExisting.lastActiveAt = userIncoming.lastActiveAt;
    } else {
      usersTemp.push(getUserFromSubscriptionData(userIncoming));
    }
    updateUsers(usersTemp);
  };

  const skip = !listingId || !me;
  useSafeListingSubscription(
    listingId,
    ListingLiveActivitySubscriptionGQL,
    (response) => {
      if (!response) {
        updateUsers([]);
        return;
      }

      const data = response.data;
      if (data.liveActivity?.users) {
        const users: ActivityUser[] = data.liveActivity.users.map((user) => getUserFromSubscriptionData(user));
        updateUsers(users);
      }
      if (data.liveActivity?.userBecameActive) {
        addOrUpdateUser(data.liveActivity.userBecameActive);
      }
      if (data.liveActivity?.userBecameInactive) {
        addOrUpdateUser(data.liveActivity.userBecameInactive);
      }
    },
    skip,
    parameters,
  );
};

export const useListingLiveActivitySubscription = (listingId: string, onUpdate: (users: ActivityUser[]) => void) => {
  const { isAdmin } = useSessionContext();
  useLiveActivitySubscription(listingId, onUpdate, isAdmin ? { isObserver: true } : undefined);
};

export const useRoomLiveActivitySubscription = (roomData: RoomDataType, onUpdate: (users: ActivityUser[]) => void) => {
  const [listingId, setListingId] = useState(undefined);

  useLiveActivitySubscription(listingId, onUpdate, { isObserver: true });

  useEffect(() => {
    if (!roomData) return;

    // we don't need to listen internal room live activity
    // or rooms without receiverId
    if (roomData?.type === RoomType.ListingInternalRoom) {
      setListingId(undefined);
      onUpdate([]);
      return;
    }

    const listingIdNew = roomData?.parentListingId || roomData?.listingId;
    if (listingIdNew === listingId) return;

    onUpdate(undefined);
    setListingId(listingIdNew);
  }, [roomData, listingId, onUpdate]);
};

export const useListingExpensesSubscription = (listingId: string, sort: ExpenseSortFieldInput) => {
  const { cache } = useApolloClient();

  // Handle new/updated expenses
  useSafeListingSubscription(listingId, ListingExpenseSubscriptionGQL, (response) => {
    const expensesCachedData: ListingExpensesQuery = cache?.readQuery({
      query: ListingExpensesDocument,
      variables: { listingId, sort },
    });

    const expensesNodesCopy = [...(expensesCachedData?.listing?.expenses || [])];
    const newExpense = response.data?.listingExpense;

    const expenseIndex = expensesNodesCopy.findIndex((exp) => exp?.id === newExpense?.id);
    if (expenseIndex !== -1) {
      expensesNodesCopy[expenseIndex] = newExpense;
    } else {
      expensesNodesCopy.unshift(newExpense);
    }

    cache?.writeQuery({
      query: ListingExpensesDocument,
      variables: { listingId, sort },
      data: {
        listing: {
          ...expensesCachedData?.listing,
          expenses: expensesNodesCopy,
        },
      },
    });
  });

  // Handle removed expenses
  useSafeListingSubscription(listingId, ListingExpenseRemovedSubscriptionGQL, (response) => {
    const expensesCachedData: ListingExpensesQuery = cache?.readQuery({
      query: ListingExpensesDocument,
      variables: { listingId, sort },
    });

    const expensesNodesCopy = [...(expensesCachedData?.listing?.expenses || [])];
    const removedExpense = response.data?.listingExpenseRemoved;

    const expenseIndex = expensesNodesCopy.findIndex((exp) => exp?.id === removedExpense?.id);
    if (expenseIndex !== -1) {
      expensesNodesCopy.splice(expenseIndex, 1);

      cache?.writeQuery({
        query: ListingExpensesDocument,
        variables: { listingId, sort },
        data: {
          listing: {
            ...expensesCachedData?.listing,
            expenses: expensesNodesCopy,
          },
        },
      });
    }
  });
};

export const useListingPurchaseInformationSubscription = (listingId: string) => {
  const { cache } = useApolloClient();

  useSafeListingSubscription(listingId, ListingPurchaseInformationSubscriptionGQL, (response) => {
    const listingCachedData: ListingQuery = cache?.readQuery({
      query: ListingDocument,
      variables: { listingId },
    });

    cache?.writeQuery({
      query: ListingDocument,
      variables: { listingId },
      data: {
        listing: {
          ...listingCachedData?.listing,
          purchaseInformation: response.data?.listingPurchaseInformation,
        },
      },
    });
  });
};

export const useListingSaleInformationSubscription = (listingId: string) => {
  const { cache } = useApolloClient();

  useSafeListingSubscription(listingId, ListingSaleInformationSubscriptionGQL, (response) => {
    const listingCachedData: ListingQuery = cache?.readQuery({
      query: ListingDocument,
      variables: { listingId },
    });

    cache?.writeQuery({
      query: ListingDocument,
      variables: { listingId },
      data: {
        listing: {
          ...listingCachedData?.listing,
          saleInformation: response.data?.listingSaleInformation,
        },
      },
    });
  });
};
